import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from '../../hooks/useNavigate';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    CardActions,
    Chip,
    LinearProgress,
    Pagination as MuiPagination,
    Grid,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import PageHeader from '../components/shared/page-header';
import { useSearchResultsController } from './context/search-results-controller';
import { useSearchResultsState } from './context/search-results-context';
import { ISearchRequest } from '../../model/base/request/search-request';
import FilterPanel, { ResultsPanel } from '../components/shared/filter-panel';

const getIconByType = (type: string) => {
  switch (type.toLowerCase()) {
    case 'news':
    case 'noticia':
      return <ArticleIcon />;
    case 'document':
    case 'documento':
      return <DescriptionIcon />;
    default:
      return <LinkIcon />;
  }
};

const getTypeLabel = (type: string) => {
  const typeMap: { [key: string]: string } = {
    news: 'Notícia',
    noticia: 'Notícia',
    document: 'Documento',
    documento: 'Documento',
    bidding: 'Licitação',
    licitacao: 'Licitação',
    daily: 'Diário Oficial',
    'diario oficial': 'Diário Oficial',
  };
  return typeMap[type.toLowerCase()] || type;
};

export function SearchResultsComponent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const { searchPortal, setSearchTerm } = useSearchResultsController();
  const { searchResults, searchTerm } = useSearchResultsState();

  const queryTerm = searchParams.get('q') || '';
  const [localSearchTerm, setLocalSearchTerm] = useState(queryTerm);
  const [paramsRequest, setParamsRequest] = useState<ISearchRequest>({
    Term: queryTerm,
    Page: 0,
    PageSize: 10,
  });

  useEffect(() => {
    const loadData = async () => {
      if (queryTerm.length >= 3) {
        setLoading(true);
        setSearchTerm(queryTerm);
        await searchPortal({ ...paramsRequest, Term: queryTerm });
        setLoading(false);
        setInitialLoading(false);
      } else {
        setInitialLoading(false);
      }
    };
    loadData();
  }, [queryTerm, paramsRequest.Page, paramsRequest.PageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (localSearchTerm.length >= 3) {
      navigate(`/busca?q=${encodeURIComponent(localSearchTerm)}`);
      setParamsRequest({ ...paramsRequest, Term: localSearchTerm, Page: 0 });
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setParamsRequest({ ...paramsRequest, Page: value - 1 });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResultClick = (url?: string) => {
    if (url) {
      if (url.startsWith('http')) {
        window.open(url, '_blank');
      } else {
        navigate(url);
      }
    }
  };

  return (
    <Box>
      <PageHeader
        title="Resultados da Busca"
        subtitle={searchTerm ? `Resultados para: "${searchTerm}"` : 'Digite ao menos 3 caracteres para buscar'}
        breadcrumbs={[{ label: 'Busca' }]}
      />

      {/* Search Form */}
      <FilterPanel>
        <form onSubmit={handleSearch}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                placeholder="Digite ao menos 3 caracteres para buscar..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                helperText={localSearchTerm.length > 0 && localSearchTerm.length < 3 ? 'Digite ao menos 3 caracteres' : ''}
                error={localSearchTerm.length > 0 && localSearchTerm.length < 3}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={localSearchTerm.length < 3}
                sx={{ height: '56px' }}
                startIcon={<SearchIcon />}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </form>
      </FilterPanel>

      {/* Results */}
      <ResultsPanel>
        {loading || initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 4 }}>
            <LinearProgress sx={{ width: '100%' }} />
            <Typography variant="body2" color="text.secondary">
              Buscando...
            </Typography>
          </Box>
        ) : queryTerm.length < 3 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <Box sx={{ textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Digite ao menos 3 caracteres para iniciar a busca
              </Typography>
            </Box>
          </Box>
        ) : !searchResults || !searchResults.items || searchResults.items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <Box sx={{ textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum resultado encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tente usar palavras-chave diferentes ou termos mais gerais
              </Typography>
            </Box>
          </Box>
        ) : (
          <React.Fragment>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchResults.totalRecords || 0} {(searchResults.totalRecords || 0) === 1 ? 'resultado encontrado' : 'resultados encontrados'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {searchResults.items.map((item, index) => (
                <Card
                  key={index}

                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ color: 'brand.text', pt: 0.5 }}>
                        {getIconByType(item.type)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Chip
                            label={getTypeLabel(item.type)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {item.category && (
                            <Chip
                              label={item.category}
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                          {item.date && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(item.date).toLocaleDateString('pt-BR')}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                          {item.title}
                        </Typography>
                        {item.excerpt && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {item.excerpt}
                          </Typography>
                        )}
                        {item.highlightedText && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontStyle: 'italic',
                              color: 'text.secondary',
                              '& mark': {
                                backgroundColor: 'warning.light',
                                padding: '2px 4px',
                                borderRadius: '2px',
                              },
                            }}
                            dangerouslySetInnerHTML={{ __html: item.highlightedText }}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                  {item.detailUrl && (
                    <CardActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => handleResultClick(item.detailUrl)}
                        endIcon={<LinkIcon />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Ver detalhes
                      </Button>
                    </CardActions>
                  )}
                </Card>
              ))}
            </Box>

            {(searchResults.pageCount || 0) > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <MuiPagination
                  count={searchResults.pageCount || 0}
                  page={(paramsRequest.Page || 0) + 1}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </React.Fragment>
        )}
      </ResultsPanel>
    </Box>
  );
}
